import { useAppSelector } from "app/hooks"
import { Fragment, useState } from "react"
import NotFound from "../NotFound"
import {
  AddMoreFoodsButton,
  FoodList,
  HintText,
  ScanMealResultsBody,
  ScanMealResultsWrapper,
} from "./styled"
import Header from "components/Layouts/Header"
import MealTypePickerFormItem from "components/mealTypeSelection/MealTypeWheelPickerFormItem"
import { useBackToHome, useSetMealTypeParams } from "features/meal/hooks"
import SearchFoods from "./SearchFoods"
import { useLogScannedFoods } from "features/meal/hooks/scanMealHooks"
import Button from "components/Buttons/Button"
import { useResetSearchFoodsState } from "features/meal/hooks"
import useScanFlowFoodOptions from "features/food/hooks/scanResultFoods/useScanFlowFoodOptions"
import FoodOption from "./FoodOption"
import PopConfirm from "components/PopConfirm"
import EdamaFoodOptionDetails from "./EdamaFoodOptionDetails"
import MyFoodOptionDetails from "./MyFoodOptionDetails"
import {
  isAIScanFoodOption,
  isAIScanNutritionFactOption,
  isAIScanPackageFoodOption,
  isEdamaFood,
  isMyFood,
  isOpenFoodFactFood,
} from "model/food/utils/typeGuards/foodTypeGuards"
import AIScanFoodDetails from "./ScanResultFoodDetails/AIScanFoodDetails"
import AIScanPackageFoodDetails from "./ScanResultFoodDetails/AIScanPackageFoodDetails"
import { AIScanFoodOptionType } from "model/food/types"
import { LogMealType } from "model/food/types/base.types"
import { ICreateMealPayload } from "model/food/types/mealPayload.types"
import getMealPayloadFromAIScanFoodOption from "model/food/utils/getPayloads/getMealPayloadFromAIScanFoodOption"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import OpenFoodFactFoodOptionDetails from "./OpenFoodFactFoodOptionDetails"

enum ScanResultSection {
  Main = "Main",
  SearchFoods = "SearchFoods",
  FoodOptionDetails = "FoodOptionDetails",
  CreatingNutritionFoodForm = "CreatingNutritionFoodForm",
}

const ScanningResult = () => {
  useResetSearchFoodsState()
  const step = useAppSelector((state) => state.meal.step)

  const [section, setSection] = useState<ScanResultSection>(
    ScanResultSection.Main,
  )

  const { mealTypeValue, setMealType } = useSetMealTypeParams()
  const {
    foods,
    aiScanResultFoods,
    manuallyAddedFoods,
    onUpdateFood,
    toggleSelectFood,
    onAddFood,
  } = useScanFlowFoodOptions()

  const [foodOptionDetails, setFoodOptionDetails] =
    useState<AIScanFoodOptionType | null>(null)
  const [isHideHeader, setIsHideHeader] = useState(false)

  const onViewFoodOptionDetails = (food: AIScanFoodOptionType) => {
    setFoodOptionDetails(food)
    setSection(ScanResultSection.FoodOptionDetails)
  }

  const backToHome = useBackToHome()
  const { handleLogScannedFoods, logScannedFoodsLoading } =
    useLogScannedFoods(backToHome)

  const scannedFoodUploadedImageDict = useAppSelector(
    (state) => state.meal.scannedFoodUploadedImageDict,
  )

  const [isPopConfirmDiscardOpen, setIsPopConfirmDiscardOpen] = useState(false)

  const isNoFoodSelected = foods.every((food) => !food.isSelected)

  const onLogSelectedFoods = () => {
    const foodsPayload: ICreateMealPayload[] = foods
      .filter((food) => food.isSelected)
      .reduce((acc, food) => {
        const payload = getMealPayloadFromAIScanFoodOption({
          food,
          logMealType: mealTypeValue,
          uploadedImageDict: scannedFoodUploadedImageDict,
        })

        if (!payload) {
          return acc
        }
        
        payload.isManualNutrition = food.isManualNutrition
        payload.creationMethod = "AI_SCAN"

        return [...acc, payload]
      }, [] as ICreateMealPayload[])

    handleLogScannedFoods(foodsPayload)
  }

  if (section === ScanResultSection.SearchFoods) {
    return (
      <ScanMealResultsWrapper>
        {!isHideHeader && (
          <Header
            isAppPaddingStyle
            onBack={() => {
              setSection(ScanResultSection.Main)
            }}
            title="Scan a meal"
          />
        )}

        <SearchFoods
          setIsHideHeader={setIsHideHeader}
          onAddFood={(food) => {
            onAddFood(food)
          }}
        />
      </ScanMealResultsWrapper>
    )
  }

  if (section === ScanResultSection.FoodOptionDetails && foodOptionDetails) {
    if (isAIScanFoodOption(foodOptionDetails)) {
      return (
        <AIScanFoodDetails
          food={foodOptionDetails}
          onUpdateFood={(food) => {
            onUpdateFood(food)
            setSection(ScanResultSection.Main)
          }}
          onBack={() => {
            setSection(ScanResultSection.Main)
            setFoodOptionDetails(null)
          }}
        />
      )
    }

    if (
      isAIScanPackageFoodOption(foodOptionDetails) ||
      isAIScanNutritionFactOption(foodOptionDetails)
    ) {
      return (
        <AIScanPackageFoodDetails
          food={foodOptionDetails}
          onUpdateFood={(food) => {
            onUpdateFood(food)
            setSection(ScanResultSection.Main)
          }}
          onBack={() => {
            setSection(ScanResultSection.Main)
            setFoodOptionDetails(null)
          }}
        />
      )
    }

    if (isEdamaFood(foodOptionDetails)) {
      return (
        <EdamaFoodOptionDetails
          onBack={() => setSection(ScanResultSection.Main)}
          food={foodOptionDetails}
          onEdit={(food) => onUpdateFood(food)}
        />
      )
    }

    if (isMyFood(foodOptionDetails)) {
      return (
        <MyFoodOptionDetails
          onBack={() => setSection(ScanResultSection.Main)}
          food={foodOptionDetails}
          onEdit={(food) => onUpdateFood(food)}
        />
      )
    }

    if (isOpenFoodFactFood(foodOptionDetails)) {
      return (
        <OpenFoodFactFoodOptionDetails
          onBack={() => setSection(ScanResultSection.Main)}
          food={foodOptionDetails}
          onEdit={(food) => onUpdateFood(food)}
        />
      )
    }
  }

  return (
    <Fragment>
      {step === "not-found" ? (
        <NotFound />
      ) : (
        <ScanMealResultsWrapper>
          <PopConfirm
            isOpen={isPopConfirmDiscardOpen}
            title="Unsaved changes?"
            cancelText="Discard"
            confirmText="Stay"
            onCancel={backToHome}
            onConfirm={() => setIsPopConfirmDiscardOpen(false)}
            loading={false}
          >
            <p style={{ textAlign: "center" }}>
              You haven't saved your meal yet. Are you sure you want to leave?
            </p>
          </PopConfirm>

          <Header
            isAppPaddingStyle
            onBack={() => {
              if (logScannedFoodsLoading) {
                return
              }

              setIsPopConfirmDiscardOpen(true)
            }}
            title="Scan a meal"
          />

          <ScanMealResultsBody>
            <MealTypePickerFormItem
              onChange={(value) => setMealType(value as LogMealType)}
              value={mealTypeValue}
            />

            <HintText>
              Here is the list of foods we've detected from your photo.
            </HintText>

            <FoodList>
              {aiScanResultFoods.map((food) => (
                <FoodOption
                  onViewDetails={onViewFoodOptionDetails}
                  food={food}
                  key={food.uuid}
                  toggleSelectFood={(food) => toggleSelectFood(food)}
                />
              ))}
            </FoodList>

            <HintText>Not seeing everything? Manually add more items</HintText>
            {!!manuallyAddedFoods.length && (
              <FoodList>
                {manuallyAddedFoods.map((food) => (
                  <FoodOption
                    onViewDetails={onViewFoodOptionDetails}
                    food={food}
                    key={food.uuid}
                    toggleSelectFood={(food) => toggleSelectFood(food)}
                  />
                ))}
              </FoodList>
            )}
            <AddMoreFoodsButton
              disabled={logScannedFoodsLoading}
              onClick={() => {
                if (!logScannedFoodsLoading) {
                  setSection(ScanResultSection.SearchFoods)
                }
              }}
              fill="outline"
              style={{
                "--border-color": "#0da3ba",
                flexShrink: 0,
              }}
            >
              <span>Add item</span>
            </AddMoreFoodsButton>
          </ScanMealResultsBody>

          <AppStyleCtaBox>
            <Button
              style={{ width: "100%" }}
              disabled={isNoFoodSelected}
              loading={logScannedFoodsLoading}
              onClick={onLogSelectedFoods}
              $type="primary"
            >
              Log this item
            </Button>
          </AppStyleCtaBox>
        </ScanMealResultsWrapper>
      )}
    </Fragment>
  )
}

export default ScanningResult
